import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import HistoryIcon from "@mui/icons-material/History";
import SendIcon from "@mui/icons-material/Send";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CodeIcon from "@mui/icons-material/Code";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AuthState } from "context/AuthContext";
import { admin } from "data/authData";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	getAnalyticsConversation,
	listAnalyticsConversations,
	sendAnalyticsChatMessage,
} from "./analyticsChatApi";
import ChatMarkdown from "./ChatMarkdown";

const PANEL_WIDTH = 400;
const PANEL_HEIGHT = 560;
const WELCOME_TEXT =
	"Hi! I'm Relook Assistant. Ask about a style, or ask count / breakdown questions like brand-wise styles or how many styles have 3XL.";

const isAssistantRole = (role) => {
	const normalized = String(role || "").toLowerCase();
	return normalized === "model" || normalized === "assistant";
};

const isAdminUser = (user) => Boolean(user?.role?.[admin] === true);

const formatRelativeTime = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleString(undefined, {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const AdminSqlDebugPanel = ({ queries, open, onToggle }) => {
	const [copiedIndex, setCopiedIndex] = useState(null);

	if (!queries?.length) return null;

	const handleCopy = async (sql, index) => {
		try {
			await navigator.clipboard.writeText(sql);
			setCopiedIndex(index);
			setTimeout(() => setCopiedIndex(null), 1500);
		} catch {
			/* ignore clipboard failures */
		}
	};

	return (
		<Box sx={{ borderTop: "1px solid", borderColor: "divider", bgcolor: "#f7f8fa" }}>
			<Button
				fullWidth
				size="small"
				onClick={onToggle}
				startIcon={<CodeIcon fontSize="small" />}
				endIcon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
				sx={{
					justifyContent: "space-between",
					px: 1.5,
					py: 0.75,
					color: "text.secondary",
					textTransform: "none",
					borderRadius: 0,
				}}>
				Admin SQL ({queries.length})
			</Button>
			<Collapse in={open}>
				<Box sx={{ px: 1.5, pb: 1.25, maxHeight: 180, overflow: "auto" }}>
					{queries.map((row, index) => {
						const sql = row?.sqlPreview || row?.sql || "";
						if (!sql) return null;
						return (
							<Box key={`${row.name || "query"}-${index}`} sx={{ mb: 1 }}>
								<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
									<Typography variant="caption" fontWeight={600} color="text.secondary">
										{row.name || "query"}
									</Typography>
									<Tooltip title={copiedIndex === index ? "Copied" : "Copy SQL"}>
										<IconButton size="small" onClick={() => handleCopy(sql, index)}>
											<ContentCopyIcon sx={{ fontSize: 14 }} />
										</IconButton>
									</Tooltip>
								</Stack>
								<Box
									component="pre"
									sx={{
										m: 0,
										p: 1,
										borderRadius: 1,
										bgcolor: "#1e1e1e",
										color: "#d4d4d4",
										fontSize: 11,
										lineHeight: 1.45,
										whiteSpace: "pre-wrap",
										wordBreak: "break-word",
										fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
									}}>
									{sql}
								</Box>
							</Box>
						);
					})}
				</Box>
			</Collapse>
		</Box>
	);
};

const MessageBubble = ({ message }) => {
	const assistant = isAssistantRole(message.role);
	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: assistant ? "flex-start" : "flex-end",
				mb: 1.25,
				gap: 1,
				alignItems: "flex-end",
			}}>
			{assistant && (
				<Box
					sx={{
						width: 28,
						height: 28,
						borderRadius: "50%",
						bgcolor: "secondary.main",
						color: "#fff",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}>
					<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
				</Box>
			)}
			<Box
				sx={{
					maxWidth: "82%",
					px: 1.5,
					py: 1,
					borderRadius: assistant ? "14px 14px 14px 4px" : "14px 14px 4px 14px",
					bgcolor: assistant ? "#f4f6f8" : "primary.main",
					color: assistant ? "text.primary" : "#fff",
					wordBreak: "break-word",
					boxShadow: assistant ? "none" : "0 1px 3px rgba(0,0,0,0.18)",
				}}>
				{assistant ? (
					<ChatMarkdown content={message.content} />
				) : (
					<Typography variant="body2" sx={{ lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
						{message.content}
					</Typography>
				)}
			</Box>
			{!assistant && (
				<Box
					sx={{
						width: 28,
						height: 28,
						borderRadius: "50%",
						bgcolor: "grey.300",
						color: "text.secondary",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}>
					<PersonOutlineIcon sx={{ fontSize: 16 }} />
				</Box>
			)}
		</Box>
	);
};

const RelookAssistantChat = () => {
	const { user } = AuthState();
	const canViewSql = isAdminUser(user);
	const [open, setOpen] = useState(false);
	const [maximized, setMaximized] = useState(false);
	const [showHistory, setShowHistory] = useState(false);
	const [showSqlDebug, setShowSqlDebug] = useState(false);
	const [debugQueries, setDebugQueries] = useState([]);
	const [input, setInput] = useState("");
	const [sending, setSending] = useState(false);
	const [loadingHistory, setLoadingHistory] = useState(false);
	const [loadingConversation, setLoadingConversation] = useState(false);
	const [error, setError] = useState("");
	const [conversationId, setConversationId] = useState(null);
	const [conversationTitle, setConversationTitle] = useState("Relook Assistant");
	const [messages, setMessages] = useState([]);
	const [conversations, setConversations] = useState([]);
	const messagesEndRef = useRef(null);
	const inputRef = useRef(null);

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, []);

	useEffect(() => {
		if (open) scrollToBottom();
	}, [messages, open, maximized, scrollToBottom]);

	useEffect(() => {
		if (!open) return;
		const timer = setTimeout(() => inputRef.current?.focus(), 180);
		return () => clearTimeout(timer);
	}, [open, maximized, conversationId]);

	const loadConversations = useCallback(async () => {
		setLoadingHistory(true);
		try {
			const data = await listAnalyticsConversations();
			setConversations(data?.result?.conversations || []);
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setLoadingHistory(false);
		}
	}, []);

	useEffect(() => {
		if (open) {
			loadConversations();
		}
	}, [open, loadConversations]);

	const resetToNewChat = () => {
		setConversationId(null);
		setConversationTitle("New conversation");
		setMessages([]);
		setError("");
		setShowHistory(false);
		setInput("");
		setDebugQueries([]);
		setShowSqlDebug(false);
	};

	const handleOpen = () => {
		setOpen(true);
		setMaximized(false);
		setError("");
	};

	const handleClose = () => {
		setOpen(false);
		setMaximized(false);
		setShowHistory(false);
		setError("");
		setShowSqlDebug(false);
	};

	const handleSelectConversation = async (id) => {
		if (!id) return;
		setLoadingConversation(true);
		setError("");
		setShowHistory(false);
		try {
			const data = await getAnalyticsConversation(id);
			const conversation = data?.result?.conversation;
			if (!conversation) {
				throw new Error("Conversation not found");
			}
			setConversationId(conversation.conversationId);
			setConversationTitle(conversation.title || "Conversation");
			setDebugQueries([]);
			setShowSqlDebug(false);
			setMessages(
				(conversation.messages || []).map((row) => ({
					role: row.role,
					content: row.content,
					createdAt: row.createdAt,
				})),
			);
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setLoadingConversation(false);
		}
	};

	const handleSend = async () => {
		const message = input.trim();
		if (!message || sending) return;

		setSending(true);
		setError("");
		setInput("");
		setMessages((prev) => [...prev, { role: "user", content: message }]);

		try {
			const data = await sendAnalyticsChatMessage({
				message,
				conversationId,
			});
			const result = data?.result || {};
			const conversation = result.conversation || {};
			const reply = result.reply || "";
			const nextId = conversation.conversationId || conversationId;

			setConversationId(nextId || null);
			setConversationTitle(conversation.title || conversationTitle || "Conversation");
			if (canViewSql) {
				const queries = Array.isArray(result?.debug?.queries) ? result.debug.queries : [];
				setDebugQueries(queries.filter((row) => row?.sqlPreview || row?.sql));
				if (queries.length) setShowSqlDebug(true);
			} else {
				setDebugQueries([]);
				setShowSqlDebug(false);
			}
			if (conversation.messages?.length) {
				setMessages(
					conversation.messages.map((row) => ({
						role: row.role,
						content: row.content,
						createdAt: row.createdAt,
					})),
				);
			} else if (reply) {
				setMessages((prev) => [...prev, { role: "model", content: reply }]);
			}
			loadConversations();
		} catch (err) {
			setError(parseErrorMessage(err));
			setMessages((prev) => {
				const next = [...prev];
				if (next.length && next[next.length - 1]?.role === "user" && next[next.length - 1]?.content === message) {
					next.pop();
				}
				return next;
			});
			setInput(message);
		} finally {
			setSending(false);
		}
	};

	const handleKeyDown = (event) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			handleSend();
		}
	};

	const headerTitle = useMemo(() => {
		if (conversationId) return conversationTitle || "Conversation";
		return "Relook Assistant";
	}, [conversationId, conversationTitle]);

	const chatBody = (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				bgcolor: "#fff",
			}}>
			<Box
				sx={{
					px: 1.5,
					py: 1.1,
					display: "flex",
					alignItems: "center",
					gap: 0.5,
					bgcolor: "primary.main",
					color: "#fff",
				}}>
				<SmartToyOutlinedIcon sx={{ fontSize: 20, mr: 0.5 }} />
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography variant="subtitle2" noWrap fontWeight={600}>
						{headerTitle}
					</Typography>
					<Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
						Ask about Relook styles
					</Typography>
				</Box>
				<Tooltip title="Chat history">
					<IconButton size="small" onClick={() => setShowHistory((v) => !v)} sx={{ color: "#fff" }}>
						<HistoryIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="New chat">
					<IconButton size="small" onClick={resetToNewChat} sx={{ color: "#fff" }}>
						<AddCommentOutlinedIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title={maximized ? "Exit full screen" : "Maximize"}>
					<IconButton size="small" onClick={() => setMaximized((v) => !v)} sx={{ color: "#fff" }}>
						{maximized ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
					</IconButton>
				</Tooltip>
				<Tooltip title="Close">
					<IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
				{showHistory && (
					<Box
						sx={{
							width: maximized ? 280 : 150,
							borderRight: "1px solid",
							borderColor: "divider",
							overflow: "auto",
							bgcolor: "#fafafa",
						}}>
						<Box sx={{ px: 1.25, py: 1 }}>
							<Typography variant="caption" color="text.secondary" fontWeight={600}>
								Recent chats
							</Typography>
						</Box>
						{loadingHistory ? (
							<Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
								<CircularProgress size={22} />
							</Box>
						) : conversations.length === 0 ? (
							<Typography variant="caption" color="text.secondary" sx={{ px: 1.5 }}>
								No conversations yet
							</Typography>
						) : (
							<List dense disablePadding>
								{conversations.map((row) => (
									<ListItemButton
										key={row.conversationId}
										selected={row.conversationId === conversationId}
										onClick={() => handleSelectConversation(row.conversationId)}
										sx={{ alignItems: "flex-start", py: 1 }}>
										<ListItemText
											primary={row.title || "Untitled"}
											secondary={formatRelativeTime(row.updatedAt)}
											primaryTypographyProps={{
												variant: "body2",
												noWrap: true,
												fontWeight: row.conversationId === conversationId ? 600 : 400,
											}}
											secondaryTypographyProps={{ variant: "caption" }}
										/>
									</ListItemButton>
								))}
							</List>
						)}
					</Box>
				)}

				<Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
					<Box sx={{ flex: 1, overflow: "auto", px: 1.75, py: 1.5 }}>
						{loadingConversation ? (
							<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
								<CircularProgress size={28} />
							</Box>
						) : messages.length === 0 ? (
							<Box sx={{ py: 3 }}>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
									{WELCOME_TEXT}
								</Typography>
								<Stack direction="row" flexWrap="wrap" gap={1}>
									{[
										"Tell me about style RR-1234",
										"How many styles have no COGS?",
										"10 recently updated styles",
										"Styles with the most sizes",
									].map((suggestion) => (
										<Chip
											key={suggestion}
											label={suggestion}
											size="small"
											variant="outlined"
											onClick={() => setInput(suggestion)}
											sx={{ cursor: "pointer" }}
										/>
									))}
								</Stack>
							</Box>
						) : (
							messages.map((message, index) => <MessageBubble key={`${message.role}-${index}`} message={message} />)
						)}
						{sending && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", mt: 1 }}>
								<CircularProgress size={14} />
								<Typography variant="caption">Thinking…</Typography>
							</Box>
						)}
						<div ref={messagesEndRef} />
					</Box>

					{error ? (
						<Alert severity="error" onClose={() => setError("")} sx={{ mx: 1.5, mb: 1 }}>
							{error}
						</Alert>
					) : null}

					{canViewSql ? (
						<AdminSqlDebugPanel
							queries={debugQueries}
							open={showSqlDebug}
							onToggle={() => setShowSqlDebug((v) => !v)}
						/>
					) : null}

					<Divider />
					<Box sx={{ p: 1.25, display: "flex", gap: 1, alignItems: "flex-end" }}>
						<TextField
							inputRef={inputRef}
							fullWidth
							multiline
							maxRows={4}
							size="small"
							placeholder="Ask about a Relook style…"
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={handleKeyDown}
							disabled={sending}
						/>
						<IconButton
							color="primary"
							onClick={handleSend}
							disabled={sending || !input.trim()}
							sx={{
								bgcolor: "primary.main",
								color: "#fff",
								"&:hover": { bgcolor: "grey.900" },
								"&.Mui-disabled": { bgcolor: "grey.300", color: "grey.500" },
							}}>
							{sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
						</IconButton>
					</Box>
				</Box>
			</Box>
		</Box>
	);

	return (
		<>
			{!open && (
				<Tooltip title="Need Help?" placement="left" arrow>
					<Fab
						color="secondary"
						aria-label="Need Help?"
						onClick={handleOpen}
						sx={{
							position: "fixed",
							right: 24,
							bottom: 24,
							zIndex: (theme) => theme.zIndex.drawer + 40,
							boxShadow: "0 8px 24px rgba(2, 119, 189, 0.35)",
						}}>
						<ChatBubbleOutlineIcon />
					</Fab>
				</Tooltip>
			)}

			{open && !maximized && (
				<Slide direction="up" in={open} mountOnEnter unmountOnExit>
					<Paper
						elevation={12}
						sx={{
							position: "fixed",
							right: 24,
							bottom: 24,
							width: { xs: "calc(100vw - 32px)", sm: PANEL_WIDTH },
							height: { xs: "min(70vh, 560px)", sm: PANEL_HEIGHT },
							zIndex: (theme) => theme.zIndex.drawer + 50,
							overflow: "hidden",
							borderRadius: 2.5,
							display: "flex",
							flexDirection: "column",
						}}>
						{chatBody}
					</Paper>
				</Slide>
			)}

			<Dialog
				fullScreen
				open={open && maximized}
				onClose={() => setMaximized(false)}
				PaperProps={{ sx: { bgcolor: "#fff" } }}>
				{chatBody}
			</Dialog>
		</>
	);
};

export default RelookAssistantChat;
